// @ts-check
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usersApi } from "@/api/endpoints/users.js";
import { masterApi } from "@/api/endpoints/master.js";
import { toast } from "@/shared/ui/toaster.jsx";

/**
 * Hooks de gestão de usuários.
 *
 * O mesmo conjunto atende a tela da clínica (`/usuarios`) e a do painel
 * Master (`/master/usuarios`): muda apenas qual cliente de API é usado.
 * Passar `escopo: "master"` troca `/api/users` por `/api/master/users`.
 *
 * @param {"clinic" | "master"} escopo
 */
function apiPara(escopo) {
  return escopo === "master" ? masterApi.users : usersApi;
}

const chave = (escopo, filtros) => ["users", escopo, filtros ?? {}];

/**
 * @param {{ escopo?: "clinic" | "master", search?: string, role?: string }} [opcoes]
 */
export function useUsers({ escopo = "clinic", ...filtros } = {}) {
  return useQuery({
    queryKey: chave(escopo, filtros),
    queryFn: async () => (await apiPara(escopo).list(filtros)).users,
    placeholderData: (anterior) => anterior,
  });
}

/** Invalida todas as listas do escopo, qualquer que seja o filtro ativo. */
function useUserMutation(escopo, mutationFn, aoTerSucesso) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (resultado) => {
      qc.invalidateQueries({ queryKey: ["users", escopo] });
      qc.invalidateQueries({ queryKey: ["master"] });
      aoTerSucesso?.(resultado);
    },
    onError: (erro) => toast.error(erro.message),
  });
}

/**
 * Cria um usuário. Quando o e-mail não pôde ser enviado, o servidor devolve
 * a senha gerada — a tela precisa exibi-la para o administrador repassar.
 * @param {"clinic" | "master"} escopo
 */
export function useCreateUser(escopo = "clinic") {
  return useUserMutation(
    escopo,
    /** @param {object} dados */
    (dados) => apiPara(escopo).create(dados),
    (resultado) => {
      // Quando o e-mail sai, a senha nunca aparece na tela. Quando não sai,
      // a própria tela mostra a senha para repasse manual — por isso aqui só
      // avisamos o sucesso, sem repetir a informação.
      if (resultado.emailSent) {
        toast.success(`Usuário criado. Credenciais enviadas para ${resultado.user.email}.`);
      } else {
        toast.warning("Usuário criado. Copie a senha exibida para repassar.");
      }
    },
  );
}

/** @param {"clinic" | "master"} escopo */
export function useUpdateUser(escopo = "clinic") {
  return useUserMutation(
    escopo,
    /** @param {{ id: string } & Record<string, unknown>} variaveis */
    ({ id, ...patch }) => apiPara(escopo).update(id, patch),
    () => toast.success("Usuário atualizado."),
  );
}

/** @param {"clinic" | "master"} escopo */
export function useResetUserPassword(escopo = "clinic") {
  return useUserMutation(
    escopo,
    /** @param {{ id: string, password?: string }} variaveis */
    ({ id, password }) => apiPara(escopo).resetPassword(id, { password }),
    (resultado) => {
      if (resultado.emailSent) toast.success("Senha redefinida e enviada por e-mail.");
      else toast.success("Senha redefinida.");
    },
  );
}

/** @param {"clinic" | "master"} escopo */
export function useDeleteUser(escopo = "clinic") {
  return useUserMutation(
    escopo,
    /** @param {string} id */
    (id) => apiPara(escopo).remove(id),
    () => toast.success("Usuário excluído."),
  );
}
